/**
 * Variable Insertion Modal Component
 *
 * Modal dialog for selecting and inserting variables into email content.
 * Provides tabbed interface for customer variables, dynamic URLs, and promo codes.
 *
 * Phase 2, Task 2.1
 * Date: 2025-10-21
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { User, Link, Tag, Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import EmailPersonalizationOptions from './EmailPersonalizationOptions';
import EmailDynamicVariableSelector from './EmailDynamicVariableSelector';
import EmailPromoCodeSelector from './EmailPromoCodeSelector';
import type { PromoCode } from '@/hooks/sms/types';

interface RestaurantDetails {
  id: string;
  name: string;
  ordering_page_url?: string;
  review_page_url?: string;
  google_review_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

export type VariableInsertionMode = 'personalization' | 'dynamic' | 'promo';

interface VariableInsertionModalProps {
  /** Whether modal is open */
  open: boolean;

  /** Callback when modal should close */
  onClose: () => void;

  /** Callback when variable is selected */
  onVariableSelect: (variable: string) => void;

  /** Callback when promo code is selected */
  onPromoCodeSelect?: (code: string, codeId: number, promoDetails?: PromoCode) => void;

  /** Available restaurants */
  restaurants?: RestaurantDetails[];

  /** Currently selected restaurant ID */
  selectedRestaurantId?: string;

  /** Callback when restaurant selection changes */
  onRestaurantChange?: (restaurantId: string) => void;

  /** Current email content (for highlighting active variables) */
  currentEmailContent?: string;

  /** Initial tab to show */
  initialMode?: VariableInsertionMode;

  /** Allow dynamic variables option */
  allowDynamicVariables?: boolean;
}

/**
 * Variable Insertion Modal
 *
 * Provides a user-friendly interface for inserting personalization variables,
 * dynamic restaurant URLs, and promo codes into email content.
 *
 * @example
 * <VariableInsertionModal
 *   open={showModal}
 *   onClose={() => setShowModal(false)}
 *   onVariableSelect={(variable) => {
 *     editor.runCommand('insert-variable', { variable });
 *     setShowModal(false);
 *   }}
 *   restaurants={userRestaurants}
 *   currentEmailContent={emailContent}
 * />
 */
const VariableInsertionModal: React.FC<VariableInsertionModalProps> = ({
  open,
  onClose,
  onVariableSelect,
  onPromoCodeSelect,
  restaurants = [],
  selectedRestaurantId,
  onRestaurantChange,
  currentEmailContent = '',
  initialMode = 'personalization',
  allowDynamicVariables = true
}) => {
  const [activeTab, setActiveTab] = useState<VariableInsertionMode>(initialMode);

  // Reset tab when modal opens
  React.useEffect(() => {
    if (open) {
      setActiveTab(initialMode);
    }
  }, [open, initialMode]);

  /**
   * Handle variable selection
   * Closes modal and passes variable to parent
   */
  const handleVariableSelect = (variable: string) => {
    onVariableSelect(variable);
    onClose();
  };

  /**
   * Handle promo code selection
   * Closes modal and passes code details to parent
   */
  const handlePromoCodeSelect = (code: string, codeId: number, promoDetails?: PromoCode) => {
    if (onPromoCodeSelect) {
      onPromoCodeSelect(code, codeId, promoDetails);
    } else {
      // Fallback: insert code as plain text
      onVariableSelect(code);
    }
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[80vh]">
        <DialogHeader>
          <DialogTitle>Insert Variable or Code</DialogTitle>
          <DialogDescription>
            Select a personalization variable, dynamic URL, or promo code to insert into your email.
          </DialogDescription>
        </DialogHeader>

        <Tabs value={activeTab} onValueChange={(value) => setActiveTab(value as VariableInsertionMode)}>
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="personalization" className="flex items-center gap-2 data-[state=active]:text-white group">
              <User className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              <span className="hidden sm:inline group-data-[state=active]:text-white">Personalization</span>
              <span className="sm:hidden group-data-[state=active]:text-white">Vars</span>
            </TabsTrigger>
            <TabsTrigger value="dynamic" className="flex items-center gap-2 data-[state=active]:text-white group">
              <Link className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              <span className="hidden sm:inline group-data-[state=active]:text-white">Dynamic URLs</span>
              <span className="sm:hidden group-data-[state=active]:text-white">URLs</span>
            </TabsTrigger>
            <TabsTrigger value="promo" className="flex items-center gap-2 data-[state=active]:text-white group">
              <Tag className="h-4 w-4 group-data-[state=active]:text-white group-data-[state=active]:stroke-white" />
              <span className="hidden sm:inline group-data-[state=active]:text-white">Promo Codes</span>
              <span className="sm:hidden group-data-[state=active]:text-white">Codes</span>
            </TabsTrigger>
          </TabsList>

          {/* Personalization Variables Tab */}
          <TabsContent value="personalization" className="mt-4">
            <ScrollArea className="h-[450px] pr-4">
              <EmailPersonalizationOptions
                onVariableSelect={handleVariableSelect}
                restaurants={restaurants}
                selectedRestaurantId={selectedRestaurantId}
                onRestaurantChange={onRestaurantChange}
                allowDynamicVariables={allowDynamicVariables}
                currentEmailContent={currentEmailContent}
              />
            </ScrollArea>
          </TabsContent>

          {/* Dynamic URLs Tab */}
          <TabsContent value="dynamic" className="mt-4 space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                <strong>Dynamic variables</strong> automatically resolve to each customer's
                preferred restaurant at send time. Perfect for multi-restaurant organizations.
              </AlertDescription>
            </Alert>

            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Select a dynamic URL variable to insert. These will automatically
                  resolve to the correct restaurant for each recipient.
                </p>

                <EmailDynamicVariableSelector
                  onVariableSelect={handleVariableSelect}
                  renderAsList={true}
                  currentEmailContent={currentEmailContent}
                  restaurants={restaurants}
                />

                <div className="border rounded-lg p-4 space-y-2 bg-muted/50">
                  <h4 className="text-sm font-semibold">How Dynamic URLs Work</h4>
                  <ol className="text-sm text-muted-foreground space-y-1 list-decimal list-inside">
                    <li>Customer's last restaurant (preferred)</li>
                    <li>Organization default URLs (fallback)</li>
                    <li>Campaign's restaurant (final fallback)</li>
                  </ol>
                </div>
              </div>
            </ScrollArea>
          </TabsContent>

          {/* Promo Codes Tab */}
          <TabsContent value="promo" className="mt-4 space-y-4">
            <Alert>
              <Info className="h-4 w-4" />
              <AlertDescription className="text-sm">
                Select an active promo code to insert. The code value will be inserted,
                and conversions will be tracked for analytics.
              </AlertDescription>
            </Alert>

            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-3">
                {/* Restaurant Selector - only show if multiple restaurants */}
                {restaurants.length > 1 && onRestaurantChange && (
                  <div className="space-y-2">
                    <Label className="text-sm text-muted-foreground">
                      Select a restaurant to view promo codes:
                    </Label>
                    <Select
                      value={selectedRestaurantId || ''}
                      onValueChange={onRestaurantChange}
                    >
                      <SelectTrigger className="w-full">
                        <SelectValue placeholder="Select a restaurant" />
                      </SelectTrigger>
                      <SelectContent>
                        {restaurants.map(restaurant => (
                          <SelectItem key={restaurant.id} value={String(restaurant.id)}>
                            {restaurant.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Info text */}
                {restaurants.length === 1 || !selectedRestaurantId ? (
                  <p className="text-sm text-muted-foreground">
                    {restaurants.length === 1
                      ? `Showing promo codes for ${restaurants[0].name}:`
                      : 'Please select a restaurant first to view promo codes.'}
                  </p>
                ) : (
                  <p className="text-sm text-muted-foreground">
                    Choose from your active promo codes below:
                  </p>
                )}

                {restaurants.length > 0 ? (
                  selectedRestaurantId || restaurants.length === 1 ? (
                    <EmailPromoCodeSelector
                      restaurantId={selectedRestaurantId || (restaurants.length === 1 ? String(restaurants[0].id) : undefined)}
                      onCodeSelect={handlePromoCodeSelect}
                      currentEmailContent={currentEmailContent}
                    />
                  ) : (
                    <Alert>
                      <Info className="h-4 w-4" />
                      <AlertDescription>
                        Please select a restaurant first to view promo codes.
                      </AlertDescription>
                    </Alert>
                  )
                ) : (
                  <Alert variant="destructive">
                    <AlertDescription>
                      No restaurants available. Please add a restaurant to use promo codes.
                    </AlertDescription>
                  </Alert>
                )}

                <div className="border rounded-lg p-4 space-y-2 bg-muted/50">
                  <h4 className="text-sm font-semibold">Promo Code Tracking</h4>
                  <p className="text-sm text-muted-foreground">
                    When customers use codes from this email, we'll track:
                  </p>
                  <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
                    <li>Which campaign the code came from</li>
                    <li>Attribution to this email send</li>
                    <li>Conversion rates and ROI</li>
                  </ul>
                </div>
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

      </DialogContent>
    </Dialog>
  );
};

export default VariableInsertionModal;
